import React from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Zap } from 'lucide-react';

const Container = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.85);
  backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  cursor: wait;
`;

const SpinnerWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
`;

const Glow = styled.div`
  position: absolute;
  width: 120px;
  height: 120px;
  background: radial-gradient(circle, rgba(59, 130, 246, 0.2) 0%, transparent 70%);
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 0.5; }
    50% { transform: scale(1.5); opacity: 0.8; }
  }
`;

const Message = styled.div`
  color: white;
  font-size: 1.25rem;
  font-weight: 700;
  text-align: center;
  letter-spacing: -0.01em;
  margin-bottom: 0.5rem;
`;

const SubMessage = styled.div`
  color: #64748b;
  font-size: 0.875rem;
  font-weight: 500;
  text-align: center;
  max-width: 300px;
  line-height: 1.6;
`;

interface LoadingOverlayProps {
  isVisible: boolean;
  message?: string;
  subMessage?: string;
  zIndex?: number;
  showHourglass?: boolean; // Kept for compatibility but using modern spinner
  actionHint?: string;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ 
  isVisible, 
  message = "Processing Request", 
  subMessage,
  actionHint
}) => {
  return (
    <AnimatePresence>
      {isVisible && (
        <Container
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <SpinnerWrapper>
            <Glow />
            <div className="relative">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin" strokeWidth={2.5} />
              <Zap className="w-4 h-4 text-blue-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 fill-blue-400 opacity-50" />
            </div>
          </SpinnerWrapper>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-center px-6"
          >
            <Message>{message}</Message>
            {subMessage && <SubMessage>{subMessage}</SubMessage>}
            {actionHint && (
              <div className="mt-8 px-4 py-1.5 bg-white/5 border border-white/10 rounded-full text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                {actionHint}
              </div>
            )}
          </motion.div>

          <div className="absolute bottom-12 flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce [animation-delay:-0.3s]" />
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce [animation-delay:-0.15s]" />
            <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-bounce" />
          </div>
        </Container>
      )}
    </AnimatePresence>
  );
};

export default LoadingOverlay;
